import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Static rates relative to USD (approximate rates).
// All monetary values are stored in USD on the backend.
// These rates are used only for display conversion and input conversion.
export const CURRENCIES = [
  { code: "USD", name: "US Dollar",        symbol: "$",  rate: 1.0    },
  { code: "EUR", name: "Euro",             symbol: "€",  rate: 0.92   },
  { code: "GBP", name: "British Pound",    symbol: "£",  rate: 0.79   },
  { code: "JPY", name: "Japanese Yen",     symbol: "¥",  rate: 155.0  },
  { code: "CAD", name: "Canadian Dollar",  symbol: "CA$",rate: 1.36   },
  { code: "AUD", name: "Australian Dollar",symbol: "A$", rate: 1.52   },
  { code: "CHF", name: "Swiss Franc",      symbol: "Fr", rate: 0.90   },
  { code: "CNY", name: "Chinese Yuan",     symbol: "¥",  rate: 7.25   },
  { code: "INR", name: "Indian Rupee",     symbol: "₹",  rate: 83.5   },
  { code: "MXN", name: "Mexican Peso",     symbol: "MX$",rate: 17.2   },
  { code: "BRL", name: "Brazilian Real",   symbol: "R$", rate: 5.05   },
  { code: "KRW", name: "South Korean Won", symbol: "₩",  rate: 1350   },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$", rate: 1.34   },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$",rate: 7.82   },
  { code: "SEK", name: "Swedish Krona",    symbol: "kr", rate: 10.5   },
  { code: "NOK", name: "Norwegian Krone",  symbol: "kr", rate: 10.6   },
  { code: "DKK", name: "Danish Krone",     symbol: "kr", rate: 6.9    },
  { code: "NZD", name: "New Zealand Dollar",symbol:"NZ$",rate: 1.63   },
  { code: "AED", name: "UAE Dirham",       symbol: "د.إ",rate: 3.67   },
  { code: "SAR", name: "Saudi Riyal",      symbol: "﷼",  rate: 3.75   },
  { code: "ZAR", name: "South African Rand",symbol:"R",  rate: 18.5   },
  { code: "NGN", name: "Nigerian Naira",   symbol: "₦",  rate: 1500   },
  { code: "GHS", name: "Ghanaian Cedi",    symbol: "GH₵",rate: 15.0  },
  { code: "KES", name: "Kenyan Shilling",  symbol: "KSh",rate: 130    },
  { code: "EGP", name: "Egyptian Pound",   symbol: "E£", rate: 47.5   },
  { code: "PKR", name: "Pakistani Rupee",  symbol: "₨",  rate: 280    },
  { code: "PHP", name: "Philippine Peso",  symbol: "₱",  rate: 57     },
  { code: "IDR", name: "Indonesian Rupiah",symbol: "Rp", rate: 16000  },
  { code: "MYR", name: "Malaysian Ringgit",symbol: "RM", rate: 4.7    },
  { code: "THB", name: "Thai Baht",        symbol: "฿",  rate: 35     },
  { code: "PLN", name: "Polish Zloty",     symbol: "zł", rate: 4.0    },
  { code: "TRY", name: "Turkish Lira",     symbol: "₺",  rate: 32.5   },
];

const CurrencyContext = createContext(null);

/** Convert an amount from fromCode currency into USD (for storage). */
export function toUSD(amount, fromCode) {
  if (!amount || isNaN(amount)) return 0;
  const c = CURRENCIES.find((x) => x.code === fromCode);
  if (!c || c.rate === 1) return +amount;
  return +(+amount / c.rate).toFixed(6);
}

/** Convert a USD amount into a display currency amount. */
export function fromUSD(usdAmount, toCode) {
  if (usdAmount === null || usdAmount === undefined || isNaN(usdAmount)) return null;
  const c = CURRENCIES.find((x) => x.code === toCode);
  if (!c || c.rate === 1) return +usdAmount;
  return +(+usdAmount * c.rate).toFixed(6);
}

/** Format a USD-stored value in the target display currency. */
export function fmtMoneyCurrency(usdAmount, toCode) {
  if (usdAmount === null || usdAmount === undefined || isNaN(usdAmount)) return "—";
  const displayAmount = fromUSD(usdAmount, toCode);
  const isRupiah = toCode === "IDR";
  return new Intl.NumberFormat(isRupiah ? "id-ID" : "en-US", {
    style: "currency",
    currency: toCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: isRupiah || Math.abs(displayAmount) >= 1000 ? 0 : 2,
  }).format(displayAmount);
}

export function CurrencyProvider({ children }) {
  const [displayCurrency, setDisplayCurrencyState] = useState(
    () => "IDR"
  );

  const setDisplayCurrency = useCallback(() => {
    setDisplayCurrencyState("IDR");
  }, []);

  /** Format a USD-stored amount in the current display currency. */
  const fmtMoney = useCallback(
    (usdAmount) => fmtMoneyCurrency(usdAmount, displayCurrency),
    [displayCurrency]
  );

  const value = useMemo(
    () => ({ displayCurrency, setDisplayCurrency, fmtMoney, CURRENCIES }),
    [displayCurrency, setDisplayCurrency, fmtMoney]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
